/**
 * Storage Utility
 * Centralizes all localStorage and cookie operations.
 */

const TOKEN_KEY = "token";
const USER_KEY = "user";

/**
 * Get Access Token
 * Checks localStorage first.
 */
export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Set Access Token
 * Stores in localStorage.
 */
export const setToken = (token: string, rememberMe: boolean = false) => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Remove Token
 * Clears access token from localStorage.
 */
export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Get User Object
 */
export const getUser = (): any => {
  const userStr = localStorage.getItem(USER_KEY);
  try {
    return userStr ? JSON.parse(userStr) : null;
  } catch (e) {
    console.error("Error parsing user from storage", e);
    return null;
  }
};

/**
 * Set User Object
 */
export const setUser = (user: any, rememberMe: boolean = false) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};


/**
 * Remove User Object
 */
export const removeUser = () => {
  localStorage.removeItem(USER_KEY);
};

/**
 * Clear All Survey Drafts
 * Removes all survey-related data from localStorage.
 */
export const clearSurveyDrafts = (surveyId?: string) => {
  if (surveyId) {
    localStorage.removeItem(`physical_vitals_draft_${surveyId}`);
    localStorage.removeItem(`driver_history_draft_${surveyId}`);
    localStorage.removeItem(`physical_exam_draft_${surveyId}`);
    localStorage.removeItem(`physical_certification_draft_${surveyId}`);
    localStorage.removeItem(`physical_tab_${surveyId}`);
  } else {
    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith("physical_vitals_draft_") ||
        key.startsWith("driver_history_draft_") ||
        key.startsWith("physical_exam_draft_") ||
        key.startsWith("physical_certification_draft_") ||
        key.startsWith("physical_tab_")
      ) {
        localStorage.removeItem(key);
      }
    });
  }
};

/**
 * Clear All Auth Data
 * Removes token and user data.
 */
export const clearAuth = () => {
  removeToken();
  removeUser();
  clearSurveyDrafts();
  sessionStorage.removeItem("hasSeenDriverWelcome");
  // We cannot clear HttpOnly cookies from client-side JS.
  // The logout endpoint should handle cleaning up cookies.
};

/**
 * Get User Role
 * Helper to extract role from stored user object.
 */
export const getRole = (): string | null => {
  const user = getUser();
  // Safe navigation for nested properties
  return user?.roles?.[user.roles.length - 1]?.name?.toLowerCase() || null;
};

// --- Cookie Helpers (if needed for non-HttpOnly cookies) ---

export const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

// Legacy/Compatibility exports if needed, or remove them.
// Keeping specific clinic helpers if they are used elsewhere.

export const setConnectedClinics = (connected_clinic: any) => {
  localStorage.setItem("clinicOfficeId", JSON.stringify(connected_clinic));
};

export const getConnectedClinics = (): any => {
  const data = localStorage.getItem("clinicOfficeId");
  try {
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};
